<template>
  <a-form
    ref="formRef"
    :model="formData"
    :rules="validationRules"
    layout="vertical"
    class="flex flex-col gap-4"
  >
    <a-form-item name="title" class="mb-0" :colon="false">
      <a-textarea
        ref="titleInputRef"
        v-model:value="formData.title"
        placeholder="Task name"
        class="borderless_input text-color proxima_nova text-[24px] font-normal"
        :auto-size="{ minRows: 1, maxRows: 3 }"
      />
    </a-form-item>

    <a-form-item name="description" class="mb-0" :colon="false">
      <a-textarea
        v-model:value="formData.description"
        placeholder="Add description..."
        :rows="4"
        class="borderless_input text-color proxima_nova text-sm font-normal"
        :auto-size="{ minRows: 4, maxRows: 8 }"
      />
    </a-form-item>

    <div v-if="schemaFields.length > 0" class="flex flex-wrap gap-2">
      <div
        v-for="field in schemaFields"
        :key="field.id || field.slug"
        class="relative group/pill"
      >
        <FieldDropdown
          :field="field"
          v-model="formData[field.slug]"
          :multiple="field.is_multi_value || false"
          max-height="280px"
          :relative-to-viewport="true"
        />
        <button
          v-if="removableFieldSlugs.includes(field.slug)"
          class="remove_custom_prop_btn"
          title="Remove field"
          @click.stop="handleRemoveCustomProp(field.slug)"
        >×</button>
      </div>

      <CustomDropdown
        v-if="hasCustomProperties"
        ref="customPickerRef"
        position="start"
        gap="4px"
        dropdownCSS="p-0"
        :class="{ 'pointer-events-none opacity-40': availableCustomAttributes.length === 0 }"
      >
        <template #trigger>
          <div
            class="add_custom_prop_btn"
            :title="availableCustomAttributes.length === 0 ? 'All custom properties added' : 'Add custom property'"
          >+</div>
        </template>
        <template #default>
          <DropdownOptions
            :options="customPropertyPickerOptions"
            :selected-values="[]"
            :show-search="availableCustomAttributes.length > 5"
            :show-unselect-all="false"
            search-placeholder="Search properties..."
            no-results-text="No properties found"
            :multiple="false"
            max-height="260px"
            @select="handleAddCustomProp"
          />
        </template>
      </CustomDropdown>
    </div>
  </a-form>
</template>

<script setup>
import { ref, reactive, watch, computed } from "vue";
import FieldDropdown from "@/src/components/task_components/FieldDropdown.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import DropdownOptions from "@/src/components/common/DropdownOptions.vue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const props = defineProps({
  users: {
    type: Array,
    default: () => [],
  },
  initialData: {
    type: Object,
    default: null,
  },
  schemaAttributes: {
    type: Array,
    default: () => [],
  },
  availableCustomAttributes: {
    type: Array,
    default: () => [],
  },
  hasCustomProperties: {
    type: Boolean,
    default: false,
  },
  removableFieldSlugs: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(["add-custom-property", "remove-custom-property"]);

const formRef = ref(null);
const titleInputRef = ref(null);
const customPickerRef = ref(null);

const formData = reactive({
  title: "",
  description: "",
});

const schemaFields = computed(() => {
  return props.schemaAttributes
    .filter((attr) => {
      if (attr.slug === "title" || attr.slug === "description") {
        return false;
      }

      return (
        attr.type === "select" ||
        attr.type === "multi_select" ||
        attr.type === "user" ||
        attr.type === "date" ||
        attr.type === "datetime"
      );
    })
    .map((attr) => {
      let options = attr.options || [];

      if (attr.type === "user") {
        options = attr.options || [];
        return {
          ...attr,
          options,
          is_multi_value: attr.is_multi_value || false,
        };
      }

      if (options.length > 0 && typeof options[0] === "string") {
        options = options.map((opt) => ({
          id: opt,
          value: opt,
          name: opt,
          label: opt,
        }));
      }

      return {
        ...attr,
        options,
        is_multi_value: attr.type === "multi_select",
      };
    });
});

const customPropertyPickerOptions = computed(() =>
  props.availableCustomAttributes.map((attr) => ({
    id: attr.slug,
    value: attr.slug,
    label: attr.name,
    name: attr.name,
  })),
);

const handleAddCustomProp = (option) => {
  const slug = option.value || option.id || option;
  emit("add-custom-property", slug);
  customPickerRef.value?.closeDropdown?.();
};

const handleRemoveCustomProp = (slug) => {
  emit("remove-custom-property", slug);
};

const validationRules = computed(() => {
  const rules = {};

  props.schemaAttributes.forEach((attr) => {
    if (attr.is_required && attr.slug !== "title") {
      rules[attr.slug] = [
        {
          required: true,
          message: `${attr.name} is required`,
          trigger:
            attr.type === "select" ||
            attr.type === "user" ||
            attr.type === "date"
              ? "change"
              : "blur",
        },
      ];
    }
  });

  return rules;
});

const validate = async () => {
  return await formRef.value.validate();
};

const getData = () => {
  const data = { ...formData };

  if (data.title && typeof data.title === "string") {
    data.title = data.title.trim();
  }
  if (data.description && typeof data.description === "string") {
    data.description = data.description.trim();
  }

  props.schemaAttributes.forEach((attr) => {
    const slug = attr.slug;
    const value = data[slug];

    if (
      (attr.type === "text" || attr.type === "textarea") &&
      value &&
      typeof value === "string"
    ) {
      data[slug] = value.trim();
    }

    if (attr.type === "date" && value) {
      if (typeof value === "object") {
        if (value.value && typeof value.value === "string") {
          data[slug] = value.value;
        } else if (value.format) {
          data[slug] = value.format("YYYY-MM-DD");
        } else if (value.date && value.date.format) {
          data[slug] = value.date.format("YYYY-MM-DD");
        }
      } else if (typeof value === "string") {
        data[slug] = value;
      }
    }

    if (attr.type === "datetime" && value) {
      let dateValue = value;
      if (typeof value === "object" && !dayjs.isDayjs(value)) {
        dateValue = value.value || value.date || null;
      }
      if (dateValue === null) {
        data[slug] = null;
      } else if (dayjs.isDayjs(dateValue)) {
        data[slug] = dateValue.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
      } else if (typeof dateValue === "string") {
        const parsed = dayjs(dateValue);
        if (parsed.isValid()) {
          data[slug] = parsed.utc().format("YYYY-MM-DDTHH:mm:ss") + "Z";
        } else {
          data[slug] = dateValue;
        }
      }
    }

    if (attr.type === "select" && value) {
      if (typeof value === "object" && value !== null) {
        data[slug] = value.value || value;
      } else {
        data[slug] = value;
      }
    }

    if (attr.type === "multi_select" && value) {
      if (Array.isArray(value)) {
        data[slug] = value.map((item) => {
          if (typeof item === "object" && item !== null) {
            return item.value || item;
          }
          return item;
        });
      } else {
        data[slug] = [];
      }
    }

    if (attr.type === "user" && value) {
      const isMultiValue = attr.is_multi_value || false;

      if (Array.isArray(value)) {
        const userIds = value
          .map((user) => {
            if (typeof user === "object" && user !== null) {
              return user.id || user.value || user;
            }
            return user;
          })
          .filter((id) => id !== null && id !== undefined);

        data[slug] = isMultiValue ? userIds : userIds[0] || null;
      } else if (typeof value === "object" && value !== null) {
        const userId = value.id || value.value || value;
        data[slug] = isMultiValue ? (userId ? [userId] : []) : userId || null;
      } else if (value) {
        data[slug] = isMultiValue ? [value] : value;
      } else {
        data[slug] = isMultiValue ? [] : null;
      }
    }
  });

  return data;
};

const reset = () => {
  formData.title = "";
  formData.description = "";

  props.schemaAttributes.forEach((attr) => {
    const slug = attr.slug;

    if (slug === "title" || slug === "description") return;

    if (attr.default_value !== undefined && attr.default_value !== null) {
      formData[slug] = attr.default_value;
    } else if (attr.type === "user") {
      formData[slug] = attr.is_multi_value ? [] : null;
    } else if (attr.type === "multi_select") {
      formData[slug] = [];
    } else if (attr.type === "date" || attr.type === "datetime") {
      formData[slug] = null;
    } else if (attr.type === "select") {
      formData[slug] = null;
    } else {
      formData[slug] = null;
    }
  });

  formRef.value?.resetFields();
};

watch(
  () => props.schemaAttributes,
  (newAttributes) => {
    if (newAttributes && newAttributes.length > 0) {
      newAttributes.forEach((attr) => {
        const slug = attr.slug;

        if (!(slug in formData)) {
          if (attr.default_value !== undefined && attr.default_value !== null) {
            formData[slug] = attr.default_value;
          } else if (attr.type === "user") {
            formData[slug] = attr.is_multi_value ? [] : null;
          } else if (attr.type === "multi_select") {
            formData[slug] = [];
          } else if (attr.type === "date" || attr.type === "datetime") {
            formData[slug] = null;
          } else if (attr.type === "select") {
            formData[slug] = null;
          } else if (attr.type === "text" || attr.type === "textarea") {
            formData[slug] = "";
          } else {
            formData[slug] = null;
          }
        }
      });
    }
  },
  { immediate: true, deep: true },
);

watch(
  () => props.initialData,
  (newData) => {
    if (newData) {
      Object.assign(formData, newData);
    }
  },
  { immediate: true },
);

const focusFirstField = () => {
  if (titleInputRef.value) {
    titleInputRef.value.focus();
  }
};

defineExpose({
  validate,
  getData,
  reset,
  focusFirstField,
});
</script>

<style scoped>
:deep(.ant-form-item-label > label) {
  color: var(--text-color);
  font-weight: 500;
}

.add_custom_prop_btn {
  display: flex;
  height: 26px;
  width: 26px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px dashed var(--white-opacity-8);
  cursor: pointer;
  color: var(--placeholder-text-color);
  background: transparent;
  font-size: 16px;
  line-height: 1;
  transition: all 0.2s ease;
  flex-shrink: 0;
  user-select: none;
}

.add_custom_prop_btn:hover {
  border-color: var(--white-opacity-12);
  background-color: var(--white-opacity-2);
}

.remove_custom_prop_btn {
  position: absolute;
  top: -6px;
  right: -6px;
  display: none;
  width: 14px;
  height: 14px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--white-opacity-12);
  color: var(--placeholder-text-color);
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
  border: none;
  padding: 0;
  transition: background 0.2s ease;
  z-index: 1;
}

.group\/pill:hover .remove_custom_prop_btn {
  display: flex;
}

.remove_custom_prop_btn:hover {
  background: var(--white-opacity-20);
  color: var(--text-color);
}
</style>
